import type { Category } from "@talkers/shared";
import { createResource, createRoot } from "solid-js";
import { api } from "@/api/client";
import { isAdmin } from "@/state/me";

/**
 * カテゴリツリーはアプリ全体で 1 度だけ取る (サイドバーと新規スレッドの選択肢で共有)。
 * `createRoot` で包むのは、root 外に computation を作った警告を出さないため。
 */
export const { categories, refetchCategories, applyThreadActivity } = createRoot(() => {
  // 管理者モードを切り替えると取り直す。false を source にすると取得自体が止まるので包む
  const [resource, { refetch, mutate }] = createResource(
    () => ({ admin: isAdmin() }),
    async ({ admin }) => (await api.listCategories(admin ? { includeHidden: true } : {})).data,
  );

  /** 木のどこかにある 1 件だけ差し替える。他の枝は同じものを使い回す。 */
  const patch = (nodes: Category[], id: number, change: (node: Category) => Category): Category[] =>
    nodes.map((node) => {
      if (node.id === id) return change(node);
      const children = patch(node.children, id, change);
      return children === node.children ? node : { ...node, children };
    });

  return {
    categories: resource,
    refetchCategories: refetch,

    /**
     * スレッドが立った / レスが付いたときの、カテゴリ側の数字だけを当てる。
     * これのために全件を取り直すと、投稿のたびにカテゴリを引くことになる。
     */
    applyThreadActivity(categoryId: number, lastPostAt: string, addedThread: boolean): void {
      const current = resource();
      if (current === undefined) return;
      mutate(
        patch(current, categoryId, (node) => ({
          ...node,
          threadCount: node.threadCount + (addedThread ? 1 : 0),
          lastPostAt,
        })),
      );
    },
  };
});

/** 木を平坦化する (slug 検索・選択肢生成に使う)。 */
export function flattenCategories(
  nodes: Category[],
  depth = 0,
): { node: Category; depth: number }[] {
  return nodes.flatMap((node) => [{ node, depth }, ...flattenCategories(node.children, depth + 1)]);
}

export function findCategoryBySlug(nodes: Category[], slug: string): Category | undefined {
  return flattenCategories(nodes).find((entry) => entry.node.slug === slug)?.node;
}

/** 親の id。トップにあれば null。 */
export function findParentId(
  nodes: Category[],
  id: number,
  parentId: number | null = null,
): number | null {
  for (const node of nodes) {
    if (node.id === id) return parentId;
    const found = findParentId(node.children, id, node.id);
    if (found !== null) return found;
  }
  return null;
}

export const SORT_ORDER_HINT = "小さいほど上。同じ値なら slug 順。0〜9999";

/** 入力欄の文字列を表示順にする。数値でなければ既定値に倒す。 */
export function parseSortOrder(value: string): number {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return 100;
  return Math.min(Math.max(parsed, 0), 9999);
}
